import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Typography,
    makeStyles,
    Paper,
    TextField,
} from '@material-ui/core';
import { ArrowDownward, ArrowLeft, ArrowRight, ArrowUpward, Settings } from '@material-ui/icons';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormGrid } from '../primitives/Forms';
import { Tooltip } from '../primitives/Tooltip';
import { useTextFilter } from './Filtering/useTextFilter';
import { FieldsSettings } from './useFields';

interface Props {
    fieldsSettings: FieldsSettings;
}

interface DialogProps extends Props {
    isOpen: boolean;
    setIsOpen: (v: boolean) => void;
}

const useStyles = makeStyles((theme) => ({
    title: {
      margin: theme.spacing(4, 2, 2),
    },
    listitemsa: {
        paddingRight: 96
    }
  }));

export const FieldSettingsPopup = ({ isOpen, setIsOpen, fieldsSettings }: DialogProps) => {
    const { includedFields, excludedFields, schema, extraSettings } = fieldsSettings;

    const { formatMessage } = useIntl();

    const fieldLabel = (f: string): string =>
        (extraSettings || {})[f]?.label
        || (schema[f]?.label_id && formatMessage({ id: schema[f].label_id}))
        || schema[f]?.label as string
        || f;

    const filter = useTextFilter<string>(f => `${fieldLabel(f)} ${f}`);

    const classes = useStyles();

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={"md"} fullWidth>
            <DialogTitle>
                <Typography variant="h6"><FormattedMessage id="fieldssettings.title" /></Typography>
            </DialogTitle>
            <DialogContent>
                <FormGrid columns="1fr 1fr" style={{ rowGap: '1rem' }}>
                    <TextField
                        label={<FormattedMessage id="searchField.label" />}
                        onChange={e => filter.setFilter(e.target.value || "")}
                        value={filter.filter}
                        />
                    <div />
                    <Paper>
                        <Typography variant="caption" className={classes.title}><FormattedMessage id="fieldssettings.available" /></Typography>
                        <List dense role="list">
                            {filter.filterData(excludedFields).map(f => (
                                <ListItem>
                                    <ListItemText primary={fieldLabel(f)} secondary={f !== fieldLabel(f) ? f : undefined} />
                                    <ListItemSecondaryAction>
                                        <IconButton size="small" edge="end" aria-label="include" onClick={() => fieldsSettings.setFieldActive(f, true)}>
                                            <ArrowRight />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                    <Paper>
                        <Typography variant="caption" className={classes.title}><FormattedMessage id="fieldssettings.active" /></Typography>
                        <List dense role="list">
                            {filter.filterData(includedFields).map(f => (
                                <ListItem dense className={classes.listitemsa}>
                                    <ListItemIcon>
                                        <IconButton size="small" aria-label="exclude" onClick={() => fieldsSettings.setFieldActive(f, false)}>
                                            <ArrowLeft />
                                        </IconButton>
                                    </ListItemIcon>
                                    <ListItemText primary={fieldLabel(f)} secondary={f !== fieldLabel(f) ? f : undefined} />
                                    <ListItemSecondaryAction>
                                        <IconButton size="small" edge="end" aria-label="include" onClick={() => fieldsSettings.moveFieldUp(f)}>
                                            <ArrowUpward />
                                        </IconButton>
                                        <IconButton size="small" edge="end" aria-label="include" onClick={() => fieldsSettings.moveFieldDown(f)}>
                                            <ArrowDownward />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </FormGrid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => fieldsSettings.resetToDefaults()}><FormattedMessage id="fieldssettings.reset" /></Button>
                <Button color="primary" onClick={() => setIsOpen(false)}><FormattedMessage id="common.close" /></Button>
            </DialogActions>
        </Dialog>
    );
}

export const FieldSettingsPopupButton = (props: Props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return <>
        <Tooltip text_id="fieldssettings.title">
            <IconButton size="small" onClick={() => setIsOpen(true)}>
                <Settings />
            </IconButton>
        </Tooltip>
        <FieldSettingsPopup isOpen={isOpen} setIsOpen={setIsOpen} {...props} />
    </>;
}
